// @ts-ignore
import Quill from '@cimpress-technology/quill/core';
import { blockAttributesMap } from '../attributes';

const Block = Quill.import('blots/block');
const Inline = Quill.import('blots/inline');

export const cachedRichTextFields: WeakMap<any, Record<any, any>> = new WeakMap();

export class Paragraph extends Block {
    constructor(context, element) {
        super(context, element);
        this.context = context;
        this.cachedBlockAttributes = cachedRichTextFields.has(context) ? cachedRichTextFields.get(context)! : {};

        const { color } = element.dataset;
        if (color) {
            this.attributes.attribute(context.registry.types.blockColor, JSON.parse(element.dataset.color));
        }

        const {
            blockColor,
            blockFontFamily,
            blockFontSize,
            blockLineHeight,
            blockCasing,
            blockLetterspacing,
            blockFontWeight,
            blockFontStyle,
            blockTextDecoration,
            blockStrokeColor,
            // blockStrokeOverprints,
            blockStrokeThickness,
            blockStrokePosition,
            blockOverprints,
        } = this.attributes.attributes;

        /**
         * If the paragraph is created without any block attributes, then we can try to set those attributes from the cache.
         * This is a band-aid for several issues which can end up unsetting those attributes, one of which is the bug in Firefox
         * in which selecting the entire contenteditable and then typing ends up blowing away all the formatting
         * and inserting a new paragraph tag instead.
         */
        const { cachedBlockAttributes } = this;
        if (!blockColor && cachedBlockAttributes.color) {
            this.attributes.attribute(context.registry.types.blockColor, cachedBlockAttributes.color);
        }
        if (!blockFontSize && cachedBlockAttributes.fontSize) {
            this.attributes.attribute(context.registry.types.blockFontSize, cachedBlockAttributes.fontSize);
        }
        if (!blockFontFamily && cachedBlockAttributes.fontFamily) {
            this.attributes.attribute(context.registry.types.blockFontFamily, cachedBlockAttributes.fontFamily);
        }
        if (!blockLineHeight && cachedBlockAttributes.lineHeight) {
            this.attributes.attribute(context.registry.types.blockLineHeight, cachedBlockAttributes.lineHeight);
        }
        if (!blockLetterspacing && cachedBlockAttributes.letterspacing) {
            this.attributes.attribute(context.registry.types.blockLetterspacing, cachedBlockAttributes.letterspacing);
        }
        if (!blockCasing && cachedBlockAttributes.casing) {
            this.attributes.attribute(context.registry.types.blockCasing, cachedBlockAttributes.casing);
        }
        if (!blockFontWeight && cachedBlockAttributes.fontWeight) {
            this.attributes.attribute(context.registry.types.blockFontWeight, cachedBlockAttributes.fontWeight);
        }
        if (!blockFontStyle && cachedBlockAttributes.fontStyle) {
            this.attributes.attribute(context.registry.types.blockFontStyle, cachedBlockAttributes.fontStyle);
        }
        if (!blockTextDecoration && cachedBlockAttributes.textDecoration) {
            this.attributes.attribute(context.registry.types.blockTextDecoration, cachedBlockAttributes.textDecoration);
        }
        if (!blockStrokeColor && cachedBlockAttributes.strokeColor) {
            this.attributes.attribute(context.registry.types.blockStrokeColor, cachedBlockAttributes.strokeColor);
        }
        /**
         * Using the cached block overprints for stroke would make it impossible to remove the stroke overprints
         * by omitting or passing an undefined value when calling setTextAreaContent
         */
        // if (!blockStrokeOverprints && cachedBlockAttributes.strokeOverprints) {
        //     this.attributes.attribute(context.registry.types.blockStrokeOverprints, cachedBlockAttributes.strokeOverprints);
        // }
        if (!blockStrokeThickness && cachedBlockAttributes.strokeThickness) {
            this.attributes.attribute(context.registry.types.blockStrokeThickness, cachedBlockAttributes.strokeThickness);
        }
        if (!blockStrokePosition && cachedBlockAttributes.strokePosition) {
            this.attributes.attribute(context.registry.types.blockStrokePosition, cachedBlockAttributes.strokePosition);
        }
        /**
         * This should likely be looking at cachedBlockAttributes.overprints instead of blockOverprints, but it is
         * avoiding the issue mentioned above for the stroke overprints, so we are leaving it as is for now.
         */
        if (!blockOverprints && cachedBlockAttributes.blockOverprints) {
            this.attributes.attribute(context.registry.types.blockOverprints, cachedBlockAttributes.blockOverprints);
        }
    }

    formatAt(index: number, length: number, name: string, value: any) {
        // Length of the block includes the newline character
        const nodeLength = this.length();
        const isWholeBlockSet = length === nodeLength - 1 || length === nodeLength;
        if (blockAttributesMap[name] && isWholeBlockSet) {
            this.cachedBlockAttributes[name] = value;
            this.format(blockAttributesMap[name], value);
        }
        cachedRichTextFields.set(this.context, this.cachedBlockAttributes);
        super.formatAt(index, length, name, value);
    }

    formats() {
        const f = super.formats();
        if (f.color) {
            delete f.color;
        }
        return f;
    }

    // We need to modify this method from Parchment to avoid a bug where replacing a selection that includes a newline character replaces the following block with an inline tag without properly maintaining all attributes
    // (Technically, the new tag does maintain any previous `style` CSS rules but will lose many other values like the `--ql-*` CSS variables and the node.dataset properties that we set with our attributors)
    public insertBefore(childBlot: any, refBlot?: any | null) {
        const isInlineBlot = childBlot instanceof Inline;
        const formats = childBlot?.formats?.() ?? {};
        if (isInlineBlot) {
            // Because `style` properties *are* maintained, we need to clear any of them whose related attributor would return an unusable .value()
            const emptyAttributes = Object.entries(formats).filter(([, val]) => !val || val === '');
            if (emptyAttributes.length && this.next) {
                // first clear the empty attributes
                emptyAttributes.forEach(([attrName]) => {
                    childBlot.attributes.attribute(this.scroll.query(attrName), null);
                });
                // then copy any existing attributes from the following Block (which will still exist when this method is called during Quill/Parchment's lifecycle despite its DOM node no longer being connected)
                this.next.attributes.copy(childBlot);
            }
        }
        super.insertBefore(childBlot, refBlot);
    }
}
